import React, { useState, useEffect } from "react";
import { FiEdit, FiTrash2, FiPlus, FiBox } from "react-icons/fi";
import {
  getMyRequests,
  createRequest,
  updateRequest,
  deleteRequest,
} from "../../../services/operations/requestAPI";

const StudentRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Modals state
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [deleteId, setDeleteId] = useState(null); // Tracks which request to delete

  const [formData, setFormData] = useState({
    class: "6",
    type: "subject",
    title: "",
    description: "",
  });

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    const res = await getMyRequests();
    if (res?.data) setRequests(res.data);
    setLoading(false);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const openCreateModal = () => {
    setIsEditing(false);
    setFormData({ class: "6", type: "subject", title: "", description: "" });
    setShowModal(true);
  };

  const openEditModal = (req) => {
    setIsEditing(true);
    setCurrentId(req._id);
    setFormData({
      class: req.class || "6",
      type: req.type,
      title: req.title,
      description: req.description,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let res = isEditing
      ? await updateRequest(currentId, formData)
      : await createRequest(formData);
    if (res) {
      setShowModal(false);
      fetchRequests();
    }
  };

  // Opens the custom delete modal
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  // Executes the actual deletion
  const confirmDelete = async () => {
    if (deleteId) {
      const res = await deleteRequest(deleteId);
      if (res) {
        fetchRequests();
      }
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">
              My Requests
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Ask for class updates, new subjects, or curriculum changes here
            </p>
          </div>
          <button
            onClick={openCreateModal}
            className="bg-[#00254D] hover:bg-[#00252D] text-white px-5 py-2.5 rounded-xl flex items-center gap-2 font-medium transition-all shadow-sm hover:shadow-blue-500/30 active:scale-95"
          >
            <FiPlus className="text-lg" /> New Request
          </button>
        </div>

        {/* Content Section */}
        {loading ? (
          /* Blueprint / Skeleton Loading State */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <div
                key={n}
                className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6 flex flex-col relative overflow-hidden animate-pulse min-h-[220px]"
              >
                {/* Decorative top line skeleton */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gray-200" />

                <div className="flex justify-between items-start mb-4 mt-2">
                  <div className="flex flex-col gap-2">
                    {/* Type skeleton */}
                    <div className="h-3 w-16 bg-gray-200 rounded"></div>
                    {/* Class badge skeleton */}
                    <div className="h-5 w-16 bg-gray-200 rounded"></div>
                  </div>
                  {/* Status badge skeleton */}
                  <div className="h-6 w-20 bg-gray-200 rounded-full"></div>
                </div>

                {/* Title skeleton */}
                <div className="h-6 w-3/4 bg-gray-200 rounded mb-3"></div>
                
                {/* Description skeleton */}
                <div className="space-y-2 mb-6">
                  <div className="h-4 w-full bg-gray-200 rounded"></div>
                  <div className="h-4 w-5/6 bg-gray-200 rounded"></div>
                  <div className="h-4 w-4/6 bg-gray-200 rounded"></div>
                </div>

                {/* Actions skeleton */}
                <div className="flex justify-end gap-2 pt-4 border-t border-gray-100 mt-auto">
                  <div className="h-8 w-8 bg-gray-200 rounded-lg"></div>
                  <div className="h-8 w-8 bg-gray-200 rounded-lg"></div>
                </div>
              </div>
            ))}
          </div>
        ) : requests.length === 0 ? (
          <div className="bg-white border border-gray-200 border-dashed rounded-2xl p-12 text-center flex flex-col items-center justify-center">
            <div className="bg-blue-50 p-4 rounded-full mb-4 text-[#00254D]">
              <FiBox size={32} />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">No requests found</h3>
            <p className="text-gray-500 text-sm max-w-sm">
              You haven't made any requests yet. Click the "New Request" button above to get started.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {requests.map((req) => (
              <div
                key={req._id}
                className="bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 p-6 flex flex-col group relative overflow-hidden"
              >
                {/* Decorative top line */}
                <div className={`absolute top-0 left-0 w-full h-1 ${
                    req.status === "approved" ? "bg-emerald-500" : req.status === "dismissed" ? "bg-rose-500" : "bg-amber-400"
                  }`}
                />

                <div className="flex justify-between items-start mb-4 mt-2">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                      {req.type}
                    </span>
                    <span className="inline-flex w-fit items-center px-2 py-0.5 rounded text-xs font-bold bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
                      Class {req.class}
                    </span>
                  </div>
                  <span
                    className={`text-xs px-2.5 py-1 rounded-full font-semibold border ${
                      req.status === "approved"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : req.status === "dismissed"
                        ? "bg-rose-50 text-rose-700 border-rose-200"
                        : "bg-amber-50 text-amber-700 border-amber-200"
                    }`}
                  >
                    {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                  </span>
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-2 leading-tight">
                  {req.title}
                </h3>
                <p className="text-gray-600 text-sm mb-6 line-clamp-3 leading-relaxed">
                  {req.description}
                </p>

                {req.adminReason && (
                  <div className="bg-gray-50/80 p-3.5 rounded-xl mb-4 border border-gray-100">
                    <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                      Admin Response
                    </p>
                    <p className="text-sm text-gray-700">{req.adminReason}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-4 border-t border-gray-100 mt-auto opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  {req.status === "pending" && (
                    <button
                      onClick={() => openEditModal(req)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <FiEdit size={18} />
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteClick(req._id)}
                    className="p-2 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <FiTrash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Sleek Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm flex justify-center items-center p-4 z-50 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col">
              <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/50">
                <h2 className="text-xl font-bold text-gray-900">
                  {isEditing ? "Edit Request" : "Submit a New Request"}
                </h2>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Class</label>
                    <select
                      name="class"
                      value={formData.class}
                      onChange={handleInputChange}
                      className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-xl p-3 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    >
                      <option value="6">Class 6</option>
                      <option value="7">Class 7</option>
                      <option value="8">Class 8</option>
                      <option value="9">Class 9</option>
                      <option value="10">Class 10</option>
                    </select>
                  </div>

                  <div className="flex-1">
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Type</label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-xl p-3 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    >
                      <option value="subject">Subject</option>
                      <option value="curriculum">Curriculum</option>
                      <option value="chapter">Chapter</option>
                      <option value="other">Other (e.g. Update Profile)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Title</label>
                  <input
                    type="text"
                    name="title"
                    required
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="E.g., Update my class to 10"
                    className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-xl p-3 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Description</label>
                  <textarea
                    name="description"
                    required
                    rows="4"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder={
                      formData.type === "other"
                        ? "Explain what you need updated (e.g. Please update my class profile from 9 to 10)"
                        : "Provide details for your request..."
                    }
                    className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-xl p-3 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-5 py-2.5 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 text-sm font-semibold bg-[#00254D] text-white rounded-xl shadow-sm active:scale-95 transition-all"
                  >
                    {isEditing ? "Save Changes" : "Submit Request"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Custom Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm flex justify-center items-center p-4 z-50 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden flex flex-col p-6 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-rose-100 mb-4">
                <FiTrash2 className="h-6 w-6 text-rose-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Request</h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to delete this request? This action cannot be undone.
              </p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-rose-600 rounded-xl hover:bg-rose-700 shadow-sm active:scale-95 transition-all"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentRequests;